/*
 * Copyright (c) 2021.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AmountProduct} from '../interfaces/AmountProduct';
import {ApiResponse} from '../interfaces/ApiResponse';
import {CartProduct} from '../interfaces/CartProduct';
import {CartProductImport} from '../interfaces/CartProductImport';
import {CartProductReplace} from '../interfaces/CartProductReplace';
import {CartProductToCartSpecification} from '../interfaces/CartProductToCartSpecification';
import {CartSpecification} from '../interfaces/CartSpecification';
import {CartSpecificationToCartSpecifications} from '../interfaces/CartSpecificationToCartSpecifications';
import {CustomProduct} from '../interfaces/CustomProduct';
import {InputFile} from '../interfaces/InputFile';
import {ProductToSpecification} from '../interfaces/ProductToSpecification';
import {RegionHeaderAmount} from '../interfaces/RegionHeaderAmount';
import {SpecificationFilter} from '../interfaces/SpecificationFilter';
import {SpecificationType} from '../interfaces/SpecificationType';
import {BaseService} from './base.service';
import {CompoApiService} from './compo-api.service';

@Injectable({
  providedIn: 'root'
})

export class SpecificationService extends BaseService {
  private updateAmountSubject$ = new Subject<CartProduct>();

  constructor(private readonly apiService: CompoApiService) {
    super();
  }

  getUpdateAmount(): Observable<CartProduct> {
    return this.updateAmountSubject$.asObservable();
  }

  nextUpdateAmount(value: CartProduct) {
    this.updateAmountSubject$.next(value);
  }

  getById(id: number): Observable<ApiResponse<CartSpecification>> {
    return this.apiService.get<CartSpecification>('/specification/' + String(id)).pipe(catchError(err => this.handleError(err)));
  }

  getByIdAndRegion(id: number, region: number): Observable<ApiResponse<CartSpecification>> {
    return this.apiService.get<CartSpecification>('/specification/' + String(id) + '?region=' + region).pipe(catchError(err => this.handleError(err)));
  }

  getAll(): Observable<ApiResponse<CartSpecification[]>> {
    return this.apiService.get<CartSpecification[]>('/specification').pipe(catchError(err => this.handleError(err)));
  }

  filter(filter: SpecificationFilter): Observable<ApiResponse<CartSpecification[]>> {
    return this.apiService.post<SpecificationFilter, CartSpecification[]>('/specification/filter', filter).pipe(
      catchError(err => this.handleError(err))
    );
  }

  getAllTypes(): Observable<ApiResponse<SpecificationType[]>> {
    return this.apiService.get<SpecificationType[]>('/specification-type').pipe(catchError(err => this.handleError(err)));
  }

  clear(specification: CartSpecification): Observable<any> {
    return this.apiService.remove('/specification/' + specification.id + '/clear').pipe(catchError(err => this.handleError(err)));
  }

  delete(specification: CartSpecification): Observable<any> {
    return this.apiService.remove('/specification/' + specification.id).pipe(catchError(err => this.handleError(err)));
  }

  createFromCart(specification: CartSpecification): Observable<ApiResponse<CartSpecification>> {
    return this.apiService.post<CartSpecification, CartSpecification>('/specification/create-from-cart', specification).pipe(catchError(err => this.handleError(err)));
  }

  createFromSpecification(specification: CartSpecification): Observable<ApiResponse<CartSpecification>> {
    return this.apiService.post<CartSpecification, CartSpecification>('/specification/create-from-specification', specification).pipe(catchError(err => this.handleError(err)));
  }

  update(specification: CartSpecification): Observable<ApiResponse<boolean>> {
    return this.apiService.put<CartSpecification, boolean>('/specification/' + specification.id, specification).pipe(catchError(err => this.handleError(err)));
  }

  deleteProduct(cartProduct: CartProduct): Observable<any> {
    return this.apiService.remove('/specification/product/' + cartProduct.id).pipe(catchError(err => this.handleError(err)));
  }

  deleteArchive(): Observable<any> {
    return this.apiService.remove('/specification/delete-archive/').pipe(catchError(err => this.handleError(err)));
  }

  archive(specifications: CartSpecification[]): Observable<any> {
    const data = specifications.map(a => a.id);
    return this.apiService.put<number[], any>('/specification/archive', data).pipe(catchError(err => this.handleError(err)));
  }

  restoreArchive(specifications: CartSpecification[]): Observable<any> {
    const data = specifications.map(a => a.id);
    return this.apiService.put<number[], any>('/specification/restore-archive', data).pipe(catchError(err => this.handleError(err)));
  }

  copy(specification: CartSpecification): Observable<ApiResponse<CartSpecification>> {
    return this.apiService.post<CartSpecification, CartSpecification>('/specification/' + specification.id + '/copy', specification).pipe(
      catchError(err => this.handleError(err))
    );
  }

  email(specifications: CartSpecification[], email: string): Observable<ApiResponse<boolean>> {
    const specificationIds = specifications.map(a => a.id);
    return this.apiService.post<any, boolean>('/specification/email', {email, specificationIds}).pipe(catchError(err => this.handleError(err)));
  }

  emailCertificate(specificationId: number, email: string): Observable<ApiResponse<boolean>> {
    return this.apiService.post<any, boolean>('/specification/' + specificationId + '/send-certificate', {email}).pipe(
      catchError(err => this.handleError(err))
    );
  }

  importFile(file: InputFile): Observable<ApiResponse<CartProduct[]>> {
    return this.apiService.postForm<InputFile, CartProduct[]>('/specification/import-file', file).pipe(catchError(err => this.handleError(err)));
  }

  importProducts(specification: CartSpecification, data: CartProductImport[]): Observable<any> {
    return this.apiService.post<CartProductImport[], any>('/specification/' + specification.id + '/import', data).pipe(
      catchError(err => this.handleError(err))
    );
  }

  importManual(data: RegionHeaderAmount): Observable<ApiResponse<CartProduct[]>> {
    return this.apiService.post<RegionHeaderAmount, any>('/specification/import-manual', data).pipe(
      catchError(err => this.handleError(err))
    );
  }

  addCustom(specification: CartSpecification, product: CustomProduct): Observable<ApiResponse<CartProduct>> {
    return this.apiService.postForm<CustomProduct, CartProduct>('/specification/' + specification.id + '/add-custom', product).pipe(
      catchError(err => this.handleError(err))
    );
  }

  addProduct(specification: CartSpecification, data: AmountProduct): Observable<ApiResponse<CartProduct>> {
    return this.apiService.post<AmountProduct, CartProduct>('/specification/' + specification.id + '/add-product', data).pipe(
      catchError(err => this.handleError(err))
    );
  }

  addProductToSpecifications(data: ProductToSpecification): Observable<ApiResponse<string>> {
    return this.apiService.post<ProductToSpecification, string>('/specification/add-product', data).pipe(
      catchError(err => this.handleError(err))
    );
  }

  recount(cartProduct: CartProduct): Observable<ApiResponse<CartProduct>> {
    return this.apiService.patch<number, CartProduct>('/specification/product/' + cartProduct.id + '/recount', cartProduct.amount).pipe(
      catchError(err => this.handleError(err))
    );
  }

  copyProductsToSpecifications(data: CartProductToCartSpecification) {
    return this.apiService.post<CartProductToCartSpecification, string>('/specification/copy', data).pipe(
      catchError(err => this.handleError(err))
    );
  }

  copySpecificationToSpecifications(data: CartSpecificationToCartSpecifications) {
    return this.apiService.post<CartSpecificationToCartSpecifications, string>('/specification/copy-specification-to-specifications', data)
      .pipe(catchError(err => this.handleError(err))
      );
  }

  replace(id: number, cartProductReplace: CartProductReplace): Observable<any> {
    return this.apiService.post<CartProductReplace, any>('/specification/' + id + '/replace', cartProductReplace);
  }
}
